// Copyright (c) 2026 Vigeos SASU. Tous droits réservés.
import React, { useEffect, useState } from 'react';
import {
  Alert, AlertTitle, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, Grid, IconButton, Paper, Stack, Table,
  TableBody, TableCell, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DescriptionIcon from '@mui/icons-material/Description';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import api from '../../utils/api';
import {
  NOT_PROVIDED, dateFr, euros, timestampFr, billingErrorMessage, percentBp, quantityFr,
  downloadDocument,
} from '../../utils/formatFacturation';
import { CreditNotesBlock } from './Avoirs';
import { STATUS_COLOR, CHANNEL_LABEL, ENTITY_KINDS, STATUS_DISPLAY, Champ } from './commun';

// Comment la facture est payée, par opposition au canal.
const METHOD_LABEL = {
  virement: 'Virement bancaire',
  prelevement_sepa: 'Prélèvement SEPA',
};

/**
 * Détail d'une facture : mentions figées, prestations, totaux, chambres
 * facturées, intérêts moratoires, avoirs et journal. Les gestes possibles
 * dépendent du statut ; ils s'ouvrent dans DialogueActionFacture (`onAction`).
 */
export default function DialogueFacture({ facture, emetteurPret: issuerReady, regime, onFermer: onClose, onAction, onRafraichir: onRefresh }) {
  const [interest, setInterest] = useState(null);
  const [interestError, setInterestError] = useState('');
  const [docError, setDocError] = useState('');
  const [interestLoading, setInterestLoading] = useState(false);

  useEffect(() => { setInterest(null); setInterestError(''); setDocError(''); }, [facture?.id]);

  if (!facture) return null;
  const f = facture;
  const isDraft = f.status === 'draft';
  const missing = f.mentions_manquantes || [];

  const readInterest = async () => {
    setInterestLoading(true); setInterestError(''); setInterest(null);
    try {
      const r = await api.get(`/invoices/${f.id}/interets`);
      setInterest(r.data);
    } catch (e) {
      setInterestError(billingErrorMessage(e, 'Intérêts non calculables'));
    } finally {
      setInterestLoading(false);
    }
  };

  // Téléchargement commun aux factures et aux avoirs (`utils/formatFacturation`) :
  // jeton dans l'en-tête, puis blob.
  const download = async (format) => {
    const label = f.number || `brouillon-${f.id.slice(0, 8)}`;
    setDocError(await downloadDocument(api, `/invoices/${f.id}`, label, format) || '');
  };

  const possibleActions = [];
  if (isDraft) {
    possibleActions.push({ type: 'emettre', label: 'Émettre', variant: 'contained', bloque: !issuerReady });
    possibleActions.push({ type: 'supprimer', label: 'Supprimer le brouillon', color: 'error' });
  }
  if (['issued', 'rejected'].includes(f.status)) possibleActions.push({ type: 'depot', label: 'Enregistrer un dépôt' });
  if (['deposited', 'issued'].includes(f.status)) possibleActions.push({ type: 'mad', label: 'Mise à disposition' });
  if (['deposited', 'available'].includes(f.status)) possibleActions.push({ type: 'rejet', label: 'Rejet', color: 'error' });
  if (!['draft', 'cancelled'].includes(f.status) && f.outstanding_cents > 0) {
    possibleActions.push({ type: 'paiement', label: 'Encaissement' });
    possibleActions.push({ type: 'relance', label: 'Tracer une relance' });
  }
  // « available » est absent des états d'où l'on peut annuler : une facture mise
  // à disposition du destinataire ne s'annule plus, elle se paie ou se rejette.
  if (['draft', 'issued', 'deposited', 'rejected'].includes(f.status)) {
    possibleActions.push({ type: 'annuler', label: 'Annuler la facture', color: 'error' });
  }

  const repartition = f.vat_breakdown || {};
  // Mention de franchise : affichée quand l'émetteur est en franchise et que
  // le document ne porte aucune TVA. Un document qui en porte (émis sous le
  // régime assujetti) se lit tel qu'il a été émis.
  const noVat = Object.values(repartition).every((v) => !Number(v))
    && (f.lines || []).every((l) => !Number(l.vat_rate_bp));
  const mentionFranchise = (!regime || regime.franchise) && noVat
    ? (regime?.mention || 'TVA non applicable, art. 293 B du CGI') : '';

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {f.number || 'Brouillon, non numérotée'}
        <Chip size="small" label={STATUS_DISPLAY[f.status] || f.status} color={STATUS_COLOR[f.status] || 'default'} />
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Recharger depuis l'API">
          <IconButton size="small" onClick={() => onRefresh(f.id)}><RefreshIcon fontSize="small" /></IconButton>
        </Tooltip>
      </DialogTitle>
      <DialogContent dividers>
        {isDraft && missing.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>Cette facture ne peut pas encore être émise</AlertTitle>
            Au brouillon, ces données se collectent encore. À l&apos;émission, il sera trop tard.
            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 3 }}>
              {missing.map((m) => <li key={m}><Typography variant="body2">{m}</Typography></li>)}
            </Box>
          </Alert>
        )}
        {isDraft && missing.length === 0 && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Aucune mention obligatoire ne manque : cette facture peut être émise.
          </Alert>
        )}
        {docError && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setDocError('')}>{docError}</Alert>}

        <Grid container spacing={2}>
          <Champ label="Établissement" xs={4}>{f.site_id}</Champ>
          <Champ label="Organisation" xs={4}>{f.organisation_id}</Champ>
          {/* Deux lignes et non une : le canal dit par quel chemin la facture
              part, le moyen dit comment elle revient payée. */}
          <Champ label="Canal de transmission" xs={4}>{CHANNEL_LABEL[f.channel] || f.channel || NOT_PROVIDED}</Champ>
          <Champ label="Moyen de paiement" xs={4}>{METHOD_LABEL[f.payment_method] || f.payment_method || NOT_PROVIDED}</Champ>
          <Champ label="Raison sociale figée" xs={4}>{f.buyer_legal_name || NOT_PROVIDED}</Champ>
          <Champ label="SIRET destinataire" xs={4}>{f.buyer_siret || NOT_PROVIDED}</Champ>
          <Champ label="Nature du client" xs={4}>{ENTITY_KINDS[f.buyer_entity_kind] || NOT_PROVIDED}</Champ>
          <Champ label="Numéro d'engagement" xs={4}>{f.engagement_number || NOT_PROVIDED}</Champ>
          <Champ label="Code service" xs={4}>{f.service_code || NOT_PROVIDED}</Champ>
          <Champ label="Numéro de marché" xs={4}>{f.market_number || NOT_PROVIDED}</Champ>
          <Champ label="Période facturée" xs={4}>{dateFr(f.period_start)} au {dateFr(f.period_end)}</Champ>
          <Champ label="Date d'exécution" xs={4}>{dateFr(f.service_date)}</Champ>
          <Champ label="Délai de paiement" xs={4}>
            {f.payment_terms_days ? `${f.payment_terms_days} jours` : NOT_PROVIDED}
          </Champ>
          <Champ label="Déposée le" xs={4}>{timestampFr(f.deposited_at)}</Champ>
          <Champ label="Mise à disposition le" xs={4}>{timestampFr(f.available_at)}</Champ>
          <Champ label="Échéance" xs={4}>{dateFr(f.due_date)}</Champ>
          <Champ label="Référence de dépôt" xs={4}>{f.external_ref || NOT_PROVIDED}</Champ>
          <Champ label="Relances tracées" xs={4}>{f.dunning_level || 0}</Champ>
          <Champ label="SIRET émetteur figé" xs={4}>{f.seller_siret || NOT_PROVIDED}</Champ>
        </Grid>

        {f.rejection_reason && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Rejetée le {timestampFr(f.rejected_at)} : {f.rejection_reason}. Aucun délai
            de paiement ne court tant qu&apos;un nouveau dépôt n&apos;a pas eu lieu.
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Prestations</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Description</TableCell>
              <TableCell align="right">Quantité</TableCell>
              <TableCell>Unité</TableCell>
              <TableCell align="right">Prix unitaire HT</TableCell>
              <TableCell align="right">Total HT</TableCell>
              <TableCell align="right">TVA</TableCell>
              <TableCell align="right">Total TTC</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(f.lines || []).length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="body2" color="error">
                    Aucune ligne : cette facture est à zéro euro. Un site sans chambre
                    active produit ce cas sans erreur.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : f.lines.map((l) => (
              <TableRow key={l.id}>
                <TableCell>{l.description}</TableCell>
                <TableCell align="right">{quantityFr(l.quantity)}</TableCell>
                <TableCell>{l.unit || ''}</TableCell>
                <TableCell align="right">{euros(l.unit_price_ht_cents)}</TableCell>
                <TableCell align="right">{euros(l.line_ht_cents)}</TableCell>
                <TableCell align="right">{percentBp(l.vat_rate_bp)}</TableCell>
                <TableCell align="right">{euros(l.line_ttc_cents)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Totaux</Typography>
              <Stack spacing={0.5}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Total hors taxes</Typography>
                  <Typography variant="body2" fontWeight="medium">{euros(f.total_ht_cents)}</Typography>
                </Box>
                {Object.keys(repartition).sort((a, b) => Number(a) - Number(b)).map((bp) => (
                  <Box key={bp} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">TVA au taux de {percentBp(bp)}</Typography>
                    <Typography variant="body2" color="text.secondary">{euros(repartition[bp])}</Typography>
                  </Box>
                ))}
                {mentionFranchise && (
                  <Typography variant="body2" color="text.secondary">{mentionFranchise}</Typography>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" fontWeight="bold">
                    {mentionFranchise ? 'Total à payer' : 'Total toutes taxes comprises'}
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">{euros(f.total_ttc_cents)}</Typography>
                </Box>
                <Divider sx={{ my: 0.5 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Encaissé</Typography>
                  <Typography variant="body2">{euros(f.paid_amount_cents)}</Typography>
                </Box>
                {/* Le total facturé ci-dessus ne bouge jamais : c'est le montant du
                    document remis, et le réécrire reviendrait à modifier une facture
                    émise. Les avoirs se lisent donc en dessous, en déduction. */}
                {f.credited_amount_cents > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Crédité par avoirs</Typography>
                    <Typography variant="body2">− {euros(f.credited_amount_cents)}</Typography>
                  </Box>
                )}
                {f.overpaid_cents > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="error.main">Trop-perçu à rendre</Typography>
                    <Typography variant="body2" color="error.main">{euros(f.overpaid_cents)}</Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color={f.outstanding_cents > 0 ? 'warning.main' : 'success.main'}>
                    Reste dû (TTC)
                  </Typography>
                  <Typography variant="body2" fontWeight="medium"
                    color={f.outstanding_cents > 0 ? 'warning.main' : 'success.main'}>
                    {euros(f.outstanding_cents)}
                  </Typography>
                </Box>
              </Stack>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                {mentionFranchise
                  ? 'Franchise en base : aucune TVA n\'est facturée, le montant à payer est le montant hors taxes.'
                  : <>Un acheteur public engage et mandate le montant TTC ; le hors taxes est
                la base contractuelle. Les deux sont donnés, aucun n&apos;est déduit de l&apos;autre à l&apos;écran.</>}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Chambres facturées : {f.rooms_billed === null || f.rooms_billed === undefined ? NOT_PROVIDED : f.rooms_billed}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Relevé figé à la création du brouillon. C&apos;est le seul compte juste :
                le compteur du back office n&apos;applique pas le même filtre.
              </Typography>
              <Box sx={{ maxHeight: 160, overflowY: 'auto' }}>
                {(f.rooms_snapshot || []).map((ch) => (
                  <Typography key={ch.id} variant="body2" sx={{ fontSize: '0.8rem' }}>
                    {ch.room_number ? `${ch.room_number} ` : ''}{ch.name || ''}{ch.unit ? ` (${ch.unit})` : ''}
                  </Typography>
                ))}
                {(f.rooms_snapshot || []).length === 0 && (
                  <Typography variant="body2" color="text.secondary">Aucune chambre au relevé.</Typography>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {f.payment_terms_text && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
            Modalités de règlement portées par la facture : {f.payment_terms_text}
          </Typography>
        )}
        {f.notes && (
          <Typography variant="body2" sx={{ mt: 1 }}>Observations : {f.notes}</Typography>
        )}

        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="subtitle2">Intérêts moratoires</Typography>
          <Button size="small" onClick={readInterest} disabled={interestLoading}>
            {interestLoading ? <CircularProgress size={14} /> : 'Calculer'}
          </Button>
        </Box>
        {interestError && (
          <Alert severity="info" sx={{ mt: 1 }}>
            <AlertTitle>Calcul impossible en l&apos;état</AlertTitle>
            {interestError}
            <Typography variant="body2" sx={{ mt: 1 }}>
              Si le taux du semestre manque, il se saisit dans l&apos;onglet
              « Impayés et taux ». Aucun taux n&apos;est deviné ici.
            </Typography>
          </Alert>
        )}
        {interest && interest.exigibles === true && (
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Champ label="Intérêts courus" xs={3}>{euros(interest.interets_cents)}</Champ>
            <Champ label="Indemnité forfaitaire" xs={3}>{euros(interest.indemnite_cents)}</Champ>
            <Champ label="Total réclamable" xs={3}>{euros(interest.total_cents)}</Champ>
            <Champ label="Taux appliqué" xs={3}>{percentBp(interest.taux_bp)}</Champ>
            <Champ label="Du" xs={3}>{dateFr(interest.depuis)}</Champ>
            <Champ label="Au" xs={3}>{dateFr(interest.jusqu_au)}</Champ>
            <Champ label="Jours de retard" xs={3}>{interest.jours_de_retard}</Champ>
            <Champ label="Assiette" xs={3}>{euros(interest.assiette_cents)} TTC</Champ>
            <Champ label="Source du taux" xs={12}>{interest.taux_source || NOT_PROVIDED}</Champ>
          </Grid>
        )}
        {interest && interest.exigibles === false && (
          <Alert severity="success" sx={{ mt: 1 }}>Aucun intérêt exigible : {interest.motif}</Alert>
        )}

        <Divider sx={{ my: 2 }} />
        {/* Les avoirs se gèrent dans le détail de la facture dont ils naissent.
            Après une émission, le détail est relu : le reste dû affiché
            au-dessus vient de changer. */}
        <CreditNotesBlock facture={f} regime={regime} onChange={() => onRefresh(f.id)} />

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Journal</Typography>
        {(f.events_log || []).length === 0 ? (
          <Typography variant="body2" color="text.secondary">Aucun évènement enregistré.</Typography>
        ) : (
          <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
            {[...(f.events_log || [])].reverse().map((ev, i) => (
              <Typography key={i} variant="body2" sx={{ fontSize: '0.8rem' }}>
                {timestampFr(ev.at)} : {ev.kind}{ev.detail ? ` , ${ev.detail}` : ''}
              </Typography>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        <Button size="small" startIcon={<DescriptionIcon />} onClick={() => download('md')}>
          Document Markdown
        </Button>
        <Button size="small" startIcon={<PictureAsPdfIcon />} onClick={() => download('pdf')}>
          Document PDF
        </Button>
        <Box sx={{ flex: 1 }} />
        {possibleActions.map((a) => (
          <Tooltip key={a.type} title={a.bloque ? 'SIRET émetteur absent : l\'émission serait refusée' : ''}>
            <span>
              <Button size="small" variant={a.variant || 'outlined'} color={a.color || 'primary'}
                disabled={Boolean(a.bloque)} onClick={() => onAction({ type: a.type, facture: f })}>
                {a.label}
              </Button>
            </span>
          </Tooltip>
        ))}
        <Button onClick={onClose}>Fermer</Button>
      </DialogActions>
    </Dialog>
  );
}
